import { useEffect, useState } from 'react';
import { Box, Button, Grid, Link, Typography } from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useTranslation } from 'react-i18next';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ErrorMessage from '../errorMessage';
import TextField from '../textField';
import { useApiContext } from '@/contexts/apiContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import { useUserContext } from '@/contexts/userContext';
import { isEmail } from 'validator';

type Props = {
  onClose: () => void;
};

const ContactUsForm = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const { user } = useUserContext();
  const { toast } = useNotificationContext();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState<any>();
  const [message, setMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();
  const [errors, setErrors] = useState<any>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user) {
      setName(`${user.firstName} ${user.lastName}`);
      setEmail(user.email);
    }
  }, [user]);

  const save = async () => {
    setError(null);
    const errors: any = {};
    let hasError = false;

    if (!executeRecaptcha) {
      setError('ReCAPTCHA not ready. Please try again.');
      return;
    }

    if (!name) {
      errors.name = t('common.errors.requiredField');
      hasError = true;
    }
    if (!email) {
      errors.email = t('common.errors.requiredField');
      hasError = true;
    }
    if (!isEmail(email)) {
      errors['email'] = 'Please enter valid email';
      hasError = true;
    }
    if (!phone) {
      errors.phone = t('common.errors.requiredField');
      hasError = true;
    }
    if (!message) {
      errors.message = t('common.errors.requiredField');
      hasError = true;
    }
    setErrors(errors);

    if (!hasError) {
      try {
        setLoading(true);
        const recaptchaToken = await executeRecaptcha('contact_form');
        await apiContext.submitContactUs({ name, email, phone, message, recaptchaToken });
        setSubmitted(true);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box
      sx={{ border: 1, borderColor: 'divider', borderRadius: '12px', overflow: 'hidden', bgcolor: 'background.paper' }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Contact Us
        </Typography>
        {error && (
          <Box sx={{ mt: 0.5 }}>
            <ErrorMessage error={error} />
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ px: 2.5, py: 2 }}>
        {!submitted && (
          <Grid container spacing={1.5}>
            <Grid size={12}>
              <TextField label="Name" value={name} required error={errors['name']} onChange={setName} maxLength={100} />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Email"
                value={email}
                required
                error={errors['email']}
                onChange={setEmail}
                maxLength={100}
              />
            </Grid>
            <Grid size={12}>
              <PhoneInput country={'us'} value={phone} onChange={setPhone} />
              {errors['phone'] && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors['phone']}
                </Typography>
              )}
            </Grid>
            <Grid size={12}>
              <TextField
                label="Message"
                value={message}
                required
                multiline
                rows={5}
                error={errors['message']}
                onChange={setMessage}
                maxLength={500}
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary">
                This site is protected by reCAPTCHA and the Google{' '}
                <Link href="https://policies.google.com/privacy" target="_blank">
                  Privacy Policy
                </Link>{' '}
                and{' '}
                <Link href="https://policies.google.com/terms" target="_blank">
                  Terms of Service
                </Link>{' '}
                apply.
              </Typography>
            </Grid>
          </Grid>
        )}
        {submitted && (
          <Typography variant="body2" color="text.secondary">
            Thank you for contacting us. Your message has been submitted. We will revert back to you as soon as
            possible.
          </Typography>
        )}
      </Box>

      {/* Action bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button variant="text" size="small" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          {submitted ? 'Close' : 'Cancel'}
        </Button>
        {!submitted && (
          <Button
            loading={loading}
            size="small"
            color="secondary"
            variant="contained"
            onClick={save}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
              color: 'text.primary',
            }}
          >
            Submit
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ContactUsForm;
